import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import * as fromStore from '../../_store';
import {Store} from '@ngrx/store';
import { INDRAPax } from '@ibe.models/pax.indra.model.lib';
import * as _ from 'lodash';
import {take} from 'rxjs/operators';
import { SelectInputOption } from '@ibe.components/common/inputs/select-input-indra/models/select-input-option';
import * as fromGlobalStore from '@ibe/store/index';
import { ValidatorsApp } from '@ibe.components/shared/_validators';
import { ScreenService } from '@ibe/services/ui';

import { PAX_TYPE_INFANT_CODE, PAX_TYPE_CHILD_CODE, PAX_TYPE_YOUNG_ADULT_CODE } from '@ibe.config';

@Component({
  selector: 'app-passenger-detail-main-info',
  templateUrl: './passenger-detail-main-info.indra.component.html',
  styleUrls: ['./passenger-detail-main-info.component.scss']
})
export class INDRAPassengerDetailMainInfoComponent implements OnInit, OnDestroy {

	@Input() error;
	@Input() displayValidationMessages: boolean = false;
	@Input() showAdditionalFields: boolean = false;
	@Input() departureDate = "";


	public form: FormGroup = this.fb.group({
		title:  [null],
		name: [null, [Validators.required, Validators.pattern('[a-zA-Z ]+')]],
		surname: [null, [Validators.required, Validators.pattern('[a-zA-Z ]+'), this.INDRA_checkMinLengthWithTrim(2)]],
		gender: [null],
		birthDate: [null],
		isNew: [true]
	}, {
		validator: this.INDRA_checkNameLength('name','surname')
	});

	public titleOptions: SelectInputOption[] = [
		{text: 'bookingDetails.label.mr', value: 'Mr'},
		{text: 'bookingDetails.label.mrs', value: 'Mrs'},
	];
	
	public genderCodes: SelectInputOption[] = [
		{text: 'bookingDetails.label.male', value: 'Male'},
		{text: 'bookingDetails.label.female', value: 'Female'}
	];
	
	public isMobile: boolean = false;
	
	private sub$ = new Subscription();
	
	private _passengerInfo: INDRAPax;
	get passengerInfo(): INDRAPax {
		return this._passengerInfo;
	}

	private _hasChanges: BehaviorSubject<boolean> = new BehaviorSubject(false);
	get hasChanges(): Observable<boolean> {
		return this._hasChanges.asObservable();
	}

	get hasErrorClassTitle(): string {
		return this.form.get('title').invalid && this.form.touched && this.displayValidationMessages ? 'has-error' : '';
	}

	get hasErrorClassFirstName(): string {
		return this.form.get('name').invalid && this.form.touched && this.displayValidationMessages ? 'has-error' : '';
	}

	get hasErrorClassLastName(): string {
		return this.form.get('surname').invalid && this.form.touched && this.displayValidationMessages ? 'has-error' : '';
	}
	
	get hasErrorClassGender(): string {
		return this.form.get('gender').invalid && this.form.touched && this.displayValidationMessages ? 'has-error' : '';
	}

	get hasData(): boolean {
		return !_.isEmpty(this.form.value);
	}

	get hasErrors(): boolean {
		return !_.isEmpty(this.form.errors);
	}
	
	get isInfant(): boolean {
		return Object.is(PAX_TYPE_INFANT_CODE, this.passengerInfo.typePax);
	}
	
	get isChild(): boolean {
		return Object.is(PAX_TYPE_CHILD_CODE, this.passengerInfo.typePax);
	}
	
	get isYoungAdult(): boolean {
		return Object.is(PAX_TYPE_YOUNG_ADULT_CODE, this.passengerInfo.typePax);
	}

	constructor(
		private fb: FormBuilder,
		private store: Store<fromStore.State>,
		private globalStore: Store<fromGlobalStore.State>,
		private screen: ScreenService
  	) { 
		this.screen.isMobile().subscribe(value => {
			this.isMobile = value;
		});
	}

	ngOnInit() {
		this.sub$.add(this.store.select(fromStore.getSelectedPassengersAtIndex)
			.subscribe( passenger => {
					if (passenger) {
						this._passengerInfo = passenger;
						this.mapPassengerToForm(passenger);
						this.updateFormValidation();
						// this.verifyFormValidations();
					}
				}
			)
		);
		this.sub$.add(
			this.form.valueChanges.subscribe(() => this._hasChanges.next(true))
		);

		this.formValueChangesUpdateMainInfo();
	}
	
	private updateFormValidation() {
		if (this.isInfant || this.isChild || this.isYoungAdult || this.showAdditionalFields) {
			this.form.get('gender').setValidators([Validators.required]);
			this.form.get('birthDate').setValidators([Validators.required]);
		} 
		
		if (!this.isInfant && !this.isChild) {
			this.form.get('title').setValidators([Validators.required]);
		}
	}
	
	private formValueChangesUpdateMainInfo() {
		this.sub$.add(
			this.form.get('title').valueChanges.subscribe(() => this.dispatchUpdateMainInfo())
		);
		this.sub$.add(
			this.form.get('name').valueChanges.subscribe((data) => {
				var regex = /^[a-zA-Z0-9_ ]+$/
				var isValid =  regex.test(data);
				if(isValid){
					this.dispatchUpdateMainInfo();
					return;
				} else{
					if(data && data !== this.INDRA_removeSpecialCharacters(data)){
						this.form.controls['name'].patchValue(this.INDRA_removeSpecialCharacters(data))
						this.dispatchUpdateMainInfo();
					}
					return;
				}
				
 
			})
		);
		this.sub$.add(
			this.form.get('surname').valueChanges.subscribe((data) => {
				var regex = /^[a-zA-Z0-9_ ]+$/
				var isValid =  regex.test(data);
				if(isValid){
					this.dispatchUpdateMainInfo();
					return;
				} else{
					if(data && data !== this.INDRA_removeSpecialCharacters(data)){
						this.form.controls['surname'].patchValue(this.INDRA_removeSpecialCharacters(data))
						this.dispatchUpdateMainInfo();
					}
					return;
				}
			})
		);
		
		// added since this passenger main info is required
		this.dispatchUpdateMainInfo();
		
		if (this.isInfant || this.isChild || this.isYoungAdult  || this.showAdditionalFields) {
			this.sub$.add(
				this.form.get('birthDate').valueChanges.subscribe(() => this.dispatchUpdateDocument())
			);
			this.sub$.add(
				this.form.get('gender').valueChanges.subscribe(() => this.dispatchUpdateDocument())
			);
			
			// added since this passenger document section (birthDate and gender field) is required for children and infant
			this.dispatchUpdateDocument();
		}
	}

	private dispatchUpdateMainInfo() {
		this.store.dispatch(
			new fromStore.UpdateFormChangedAction({ 'updateMainInfo': true })
		);
	}
	
	private dispatchUpdateDocument() {
		this.store.dispatch(
			new fromStore.UpdateFormChangedAction({ 'updateDocument': true })
		);
	}

	private mapPassengerToForm(data: INDRAPax) {
		// note: a surname with number indicates the initial pax coming from a dummy PNR (pre-pax creation)
		this.form.patchValue({ 	
			title : data.title,
			name: (data.surname && isNaN(data.surname as any)) ? data.name : '',
			surname: (data.surname &&isNaN(data.surname as any)) ? data.surname : '',
			gender: (data.listTravelDoc && data.listTravelDoc[0]) ? data.listTravelDoc[0].gender : null,
			birthDate: (data.listTravelDoc && data.listTravelDoc[0]) ? data.listTravelDoc[0].birthDate : null,
			isNew: data.isNew
		}, {emitEvent: false});
	}

	ngOnDestroy() {
		this.sub$.unsubscribe();
	}

	public trackByFn(index: number, item: any): any {
		return index;
	}

	public formMainInfoErrors(control: string) {
		const errors = [];
		if (Object.is('title', control)) {
			if (this.form.controls[control].hasError('required')) {
				errors.push('bookingDetails.error.titleRequired');
			}
		}
		if (Object.is('name', control)) {
			if (this.form.controls[control].hasError('pattern')) {
				errors.push('bookingDetails.error.firstNameInvalid');
			} else if (this.form.controls['surname'].hasError('pattern') && this.isMobile) {
				errors.push('bookingDetails.error.lastNameInvalid');
			} else if ((this.form.controls[control].hasError('required') || this.form.controls['surname'].hasError('required')) && this.isMobile) {
				errors.push('bookingDetails.error.firstNameLastNameRequired');
			} else if (this.form.controls[control].hasError('required') && !this.isMobile) {
				errors.push('bookingDetails.error.firstNameRequired');
			}
		}
		
		if (Object.is('surname', control)) {
			if (this.form.controls[control].hasError('required')) {
				errors.push('bookingDetails.error.lastNameRequired');
			}
			if (this.form.controls[control].hasError('pattern')) {
				errors.push('bookingDetails.error.lastNameInvalid');
			}
			if (this.form.controls[control].hasError('minlength')) {
				errors.push('bookingDetails.error.lastNameMoreThanChar');
			}
		}

		if (Object.is('gender', control)) {
			if (this.form.controls[control].hasError('required')) {
				errors.push('bookingDetails.error.personalDocGenderRequired');
			}
		}
		if (Object.is('birthDate', control)) {
			if (this.form.controls[control].hasError('required')) {
				errors.push('bookingDetails.error.personalDocFullBirthDateRequired');
			}
			if (this.form.controls[control].hasError('invalidDate')) {
				errors.push('bookingDetails.error.personalDocFullBirthDateRequired');
			}
		}
		if (Object.is('maxNameCharReached', control)) {
			if (this.form.hasError(control)) {
				errors.push('bookingDetails.error.maxNameCharExceeded');
			}
		}
		return errors.join('; ');
	}

	public clearForm() {
		this.form.reset({emitEvent: false});
	}

	public cancelEdit() {
		this.store.select(fromStore.getSelectedPassengersAtIndex)
			.pipe(take(1)).subscribe((data: INDRAPax) => {
				this.form.reset({
					title : data.title,
					name: data.name,
					surname: data.surname 
				}, {emitEvent: false});

				this._hasChanges.next(false);
			});
		this.store.dispatch(
			new fromStore.UpdateFormChangedAction(
				{
					'updateMainInfo': false,
				}
			)
		);
	}


	INDRA_removeSpecialCharacters(value){
		return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
	}

	INDRA_checkNameLength(firstNameControl, secondNameControl): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			if (control.get(firstNameControl).value && control.get(secondNameControl).value) {
				const firstname = control.get(firstNameControl).value.length;
				const lastname = control.get(secondNameControl).value.length;

				// max characters for combined first and last name should not exceed 31 characters
				if (firstname + lastname > 31) { 
					return { 'maxNameCharReached': true }; 
				} else {
					return null;
				}
			}
		}
	}
	
	INDRA_checkMinLengthWithTrim(minimumLength: number): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			if (control.value && control.value.trim().length < minimumLength) {
				return { 'minlength': true }; 
			}
			return null;
		}
	}
}
